/**
 * Shared single source of truth for project & stage progress calculations.
 *
 * All progress numbers displayed in headers, cards, tabs, and detail views
 * MUST use these helpers to guarantee identical values everywhere.
 *
 * Formula:
 *   projectProgress = (scheduleFinalProgress * 0.5) + (checklistFinalProgress * 0.5)
 *
 * Where:
 *   scheduleFinalProgress = count(is_complete = true) / totalItems  (BINARY)
 *   checklistFinalProgress = count(all required checks done) / totalItems
 *
 * Important: schedule progress is BINARY — only items with is_complete=true count.
 * The item.progress field (0-100 average) is displayed separately but never used
 * for the "Final" percentage.
 */

import { getMainStage, UNASSIGNED_STAGE } from '@/lib/scheduleStageMapping';

export interface StageProgressSummary {
  totalItems: number;
  completedItems: number; // only is_complete = true
  binaryPct: number;
  avgProgress: number; // average of item.progress fields
  checklistFinalPct: number;
  checklistFinalCompleted: number;
  checklistFinalTotal: number;
  combinedPct: number;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Delayed';
}

export interface ProjectProgressResult {
  scheduleFinalPct: number;
  scheduleTotalItems: number;
  scheduleCompletedItems: number;
  checklistFinalPct: number;
  checklistFinalCompleted: number;
  checklistFinalTotal: number;
  combinedPct: number;
  /** Per-stage breakdown */
  stageProgress: Record<string, StageProgressSummary>;
  /** Current active stage name (first non-completed by sort order) */
  currentStageName: string;
}

interface ScheduleItemLike {
  id: string;
  title?: string;
  is_complete: boolean;
  progress: number;
  main_stage?: string | null;
  phase?: string | null;
  source_template_item_id?: string | null;
  delay_status?: string | null;
  is_included?: boolean;
  /** Optional: actual end date — counts as complete if set */
  actual_end_date?: string | null;
  /** Optional: manual completion override */
  manual_complete?: boolean;
  /** Optional: completed_at timestamp */
  completed_at?: string | null;
}

interface ChecklistItemLike {
  id: string;
  management_checked: boolean;
  requires_management_check?: boolean;
  subcontractor_checked: boolean;
  requires_subcontractor_check?: boolean;
}

const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];

/**
 * UNIFIED COMPLETION CHECK — Single Source of Truth for schedule completion.
 *
 * Returns TRUE if the item is considered "complete" by ANY of these criteria:
 *   - is_complete === true                (DB manual flag)
 *   - progress >= 100                     (progress slider at 100)
 *   - manual_complete === true              (explicit override flag)
 *   - completed_at != null                  (timestamp set)
 *   - actual_end_date != null               (actual end date recorded)
 *   - ALL linked checklist items are fully approved (when linkedItems provided)
 *
 * Returns FALSE if NONE of the above are true.
 *
 * IMPORTANT: "no checklist mapped" does NOT affect completion.
 * Mapping status is a SEPARATE concept from completion status.
 * A manually completed item with no checklist mapping is still COMPLETE.
 */
export function isScheduleItemComplete(si: ScheduleItemLike, linkedItems?: ChecklistItemLike[]): boolean {
  if (si.is_complete) return true;
  if (si.progress >= 100) return true;
  if (si.manual_complete) return true;
  if (si.completed_at != null && si.completed_at !== '') return true;
  if (si.actual_end_date != null && si.actual_end_date !== '') return true;

  // If linked checklist items exist and ALL are fully approved, item counts as complete
  if (linkedItems && linkedItems.length > 0) {
    const allApproved = linkedItems.every(isChecklistFinalComplete);
    if (allApproved) return true;
  }

  return false;
}

/**
 * Get a human-readable reason for why an item is NOT complete.
 * Used in debug output and UI tooltips.
 */
export function getScheduleItemPendingReason(si: ScheduleItemLike): string {
  if (isScheduleItemComplete(si)) return '';
  if (si.is_included === false) return 'Excluded';
  if (si.progress > 0) return `In progress (${si.progress}%)`;
  return 'Not started';
}

function resolveItemStage(item: ScheduleItemLike): string {
  return getMainStage(item);
}

function isChecklistFinalComplete(item: ChecklistItemLike): boolean {
  const needsMgmt = item.requires_management_check !== false;
  const needsSub = item.requires_subcontractor_check === true;
  const mgmtOk = !needsMgmt || item.management_checked;
  const subOk = !needsSub || item.subcontractor_checked;
  return mgmtOk && subOk;
}

/**
 * Calculate project-level and per-stage progress from schedule items and checklist items.
 *
 * @param scheduleItems — all schedule items for the project
 * @param checklistItems — all checklist items for the project
 * @param linkedChecklists — map of scheduleItemId -> checklist items linked to it
 */
export function calculateProjectProgress(
  scheduleItems: ScheduleItemLike[],
  checklistItems: ChecklistItemLike[],
  linkedChecklists: Map<string, ChecklistItemLike[]> = new Map()
): ProjectProgressResult {
  // --- Stage-level aggregation ---
  const stageMap = new Map<string, {
    scheduleItems: ScheduleItemLike[];
    checklistItems: ChecklistItemLike[];
  }>();

  for (const name of STAGE_ORDER) {
    stageMap.set(name, { scheduleItems: [], checklistItems: [] });
  }

  // Group schedule items by stage
  for (const item of scheduleItems) {
    if (item.is_included === false) continue;
    const stage = resolveItemStage(item);
    const s = stageMap.get(stage);
    if (s) s.scheduleItems.push(item);
    else if (stage === UNASSIGNED_STAGE) {
      // Unassigned items do not affect canonical stage progress buckets.
    }
  }

  // Group checklist items by stage via linked schedule items
  const checklistItemToStage = new Map<string, string>();
  for (const [scheduleItemId, linked] of linkedChecklists) {
    const si = scheduleItems.find((s) => s.id === scheduleItemId);
    if (!si) continue;
    const stage = resolveItemStage(si);
    for (const ci of linked) {
      checklistItemToStage.set(ci.id, stage);
    }
  }

  // Also include unmapped checklist items — assign to 'Planning' as fallback
  for (const ci of checklistItems) {
    const stage = checklistItemToStage.get(ci.id) || 'Planning';
    const s = stageMap.get(stage);
    if (s && !s.checklistItems.some((x) => x.id === ci.id)) {
      s.checklistItems.push(ci);
    }
  }

  // Compute per-stage progress
  const stageProgress: Record<string, StageProgressSummary> = {};
  for (const name of STAGE_ORDER) {
    const s = stageMap.get(name)!;
    const totalItems = s.scheduleItems.length;
    // CRITICAL: Use unified completion check with linked items for stage-level calculation
    const completedItems = s.scheduleItems.filter((si) => {
      const linked = linkedChecklists.get(si.id) || [];
      return isScheduleItemComplete(si, linked);
    }).length;
    const binaryPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
    const avgProgress = totalItems > 0
      ? Math.round(s.scheduleItems.reduce((sum, i) => sum + i.progress, 0) / totalItems)
      : 0;

    const checklistFinalTotal = s.checklistItems.length;
    const checklistFinalCompleted = s.checklistItems.filter(isChecklistFinalComplete).length;
    const checklistFinalPct = checklistFinalTotal > 0
      ? Math.round((checklistFinalCompleted / checklistFinalTotal) * 100)
      : 0;

    const combinedPct = Math.round(binaryPct * 0.5 + checklistFinalPct * 0.5);

    // Stage status
    let status: StageProgressSummary['status'] = 'Not Started';
    if (binaryPct === 100 && checklistFinalPct === 100) {
      status = 'Completed';
    } else if (combinedPct > 0 && combinedPct < 100) {
      const anyDelayed = s.scheduleItems.some((i) => i.delay_status === 'delayed' || i.delay_status === 'completed_late');
      status = anyDelayed ? 'Delayed' : 'In Progress';
    } else if (combinedPct === 0) {
      status = 'Not Started';
    }

    stageProgress[name] = {
      totalItems,
      completedItems,
      binaryPct,
      avgProgress,
      checklistFinalPct,
      checklistFinalCompleted,
      checklistFinalTotal,
      combinedPct,
      status,
    };
  }

  // --- Project-level ---
  const includedScheduleItems = scheduleItems.filter((i) => i.is_included !== false);
  const scheduleTotalItems = includedScheduleItems.length;
  // CRITICAL FIX: Use unified completion check WITH linked items
  const scheduleCompletedItems = includedScheduleItems.filter((si) => {
    const linked = linkedChecklists.get(si.id) || [];
    return isScheduleItemComplete(si, linked);
  }).length;
  const scheduleFinalPct = scheduleTotalItems > 0
    ? Math.round((scheduleCompletedItems / scheduleTotalItems) * 100)
    : 0;

  const checklistFinalTotal = checklistItems.length;
  const checklistFinalCompleted = checklistItems.filter(isChecklistFinalComplete).length;
  const checklistFinalPct = checklistFinalTotal > 0
    ? Math.round((checklistFinalCompleted / checklistFinalTotal) * 100)
    : 0;

  const combinedPct = Math.round(scheduleFinalPct * 0.5 + checklistFinalPct * 0.5);

  // Current stage: first stage not completed, by STAGE_ORDER
  let currentStageName = 'Handover';
  for (const name of STAGE_ORDER) {
    const sp = stageProgress[name];
    if (sp.status !== 'Completed') {
      currentStageName = name;
      break;
    }
  }

  // CRITICAL FIX: Detailed debug log for every calculation
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('Project Progress Summary (calculateProjectProgress)', {
      scheduleFinalProgress: scheduleFinalPct,
      checklistFinalProgress: checklistFinalPct,
      projectProgress: combinedPct,
      scheduleCompletedItems,
      scheduleTotalItems,
      scheduleItemsDetail: includedScheduleItems.map((si) => {
        const linked = linkedChecklists.get(si.id) || [];
        return {
          id: si.id,
          is_complete: si.is_complete,
          progress: si.progress,
          actual_end_date: si.actual_end_date,
          manual_complete: si.manual_complete,
          completed_at: si.completed_at,
          linkedCount: linked.length,
          isScheduleItemComplete: isScheduleItemComplete(si, linked),
          pendingReason: getScheduleItemPendingReason(si),
        };
      }),
    });
  }

  return {
    scheduleFinalPct,
    scheduleTotalItems,
    scheduleCompletedItems,
    checklistFinalPct,
    checklistFinalCompleted,
    checklistFinalTotal,
    combinedPct,
    stageProgress,
    currentStageName,
  };
}

/**
 * Lightweight version when you only have checklist items (no schedule data).
 */
export function calculateChecklistProgressOnly(
  checklistItems: ChecklistItemLike[]
): Pick<ProjectProgressResult, 'checklistFinalPct' | 'checklistFinalCompleted' | 'checklistFinalTotal'> {
  const total = checklistItems.length;
  const completed = checklistItems.filter(isChecklistFinalComplete).length;
  return {
    checklistFinalCompleted: completed,
    checklistFinalTotal: total,
    checklistFinalPct: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
}

/**
 * Lightweight version when you only have schedule items (no checklist data).
 */
export function calculateScheduleProgressOnly(
  scheduleItems: ScheduleItemLike[]
): Pick<ProjectProgressResult, 'scheduleFinalPct' | 'scheduleTotalItems' | 'scheduleCompletedItems'> {
  const included = scheduleItems.filter((i) => i.is_included !== false);
  const total = included.length;
  const completed = included.filter(isScheduleItemComplete).length;
  return {
    scheduleTotalItems: total,
    scheduleCompletedItems: completed,
    scheduleFinalPct: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
}